import { ActionTracker } from "src/common/managers/ActionTracker";
import { StatTracker } from "src/common/managers/StatTracker";

export enum WindowId {
  ShowSearchField,
  InformationWindow,
  GettingStarted,
  ShowReleases,
  Legend,
  ShowLegendButton,
  DemographyReportDialog,
  StreetView,
  SelectCircle,
  GenerateReportDialog,
  GenerateReportResultDialog,
  PropertyInformationDialog,
  CompanyInformationDialog,
  ObliqueViewer,
  SaveMapDialog,
  SaveLinkDialog,
  SavedLinkDialog,
  CatchmentSelector,
  SpatialSelection,
  CustomerSettingsEditor,
  UserSettingsEditor,
  MapSettingsEditor,
  PointDistanceTable,
  ConfirmLayerList,
  FileTooBig,
  NameOnCreation,
  NameOnExport,
  DownloadLayerDialog,
  GDPRNoticeApproval,
  Explore,
  PlotScreen,
  LoadedSettings,
  UserAccessRights,
  SimulateOtherUser,
  BNBOMarkAreaEditor,
  CloudSaveDialog,
  ViewCloudSaveDialog,
  AdminCloudSaveDialog,
  PersonInformationDialog,
  OwnerShipDiagram,
  CondominiumDialog,
  MapLinkOverview,
  BNBODialog,
  BNBOAreaEditor,
  BNBOSingleElement,
  BNBOSFEStatus,
  BNBOOpgørelse,
  BNBOAreaViewer,
  EjendomskortDialog,
  ChatWindow,
  JobsDialog,
  JobsAdminDialog,
  JobServiceInsider,
  JobServiceLog,
  JobServiceJobCreator,
  JobServiceTaskList,
  JobServiceTaskInfo,
  JobServiceMailExample,
  ConfirmationDialog,
  ImportPrevSettings,
  MapDescription,
  BNBOStatusEditor,
  ServituterklaeringCreator,
  ServituterklaeringEditor,
  MapPlotLogoEditor,
  Servituterklaering,
  ServitutDetails,
  DemoSideComponents,
  DemoSideIconsMIB,
  BroadcastReceiverDebug,
  PopupSettingsCustomizer,
  ConflictSearchDialog,
  JPBTool,
  ConflictSearchResults,
  AdkomstDetails,
  FRPSearch,
  FRPImport,
  FRPStatusEditor,
  FRPTagEditor,
  FRPMainDialog,
  FRPAnnoteringsDialog,
  FRPDocuments,
  FRPExportToExcel,
  FRPAdminDialog,
  Opslag,
  ImageOverlayDebug,
  GraphQLDemo,
  PDFInspector,
  ANNEditor,
  ANNUnverifiedAnnotations,
  ANNPDFAnalyzerViewer,
  DebugLayerTester,
  DebugLayerTesterEditor,
  JPBIndexBuilder,
  JPBTimeHorizon,
  JPBPriceIndexSource,
  JPBSkovDemo,
  DownloadManager,
  ConflictReportDialog,
  LayerFunctions,
  ServitutRedegoerelse,
  // Add new windows here
}

export enum WindowState {
  Open = "open",
  Minimized = "mini",
  Closed = "closed",
  Closing = "closing"
}

export interface ManagedWindow {
  id: WindowId;
  uid?: string;
  state: WindowState;
  updateCounter: number;
  windowParams?: any;
  alwayMounted?: boolean;
  asPopop?: boolean;
  adm?: any;
}

export interface State {
  windows: ManagedWindow[];
}

// ACTIONS
export enum WindowStateActions {
  Open,
  Close,
  Minimize,
  Focus,
  SendParams,
  Disconnect,
  OpenAsPopup,
  setADMProps,
  PopIn,
}
export interface SetADM {
  type: WindowStateActions.setADMProps;
  windowId: WindowId | string;
  params: { admProps: any };
}

export interface Open {
  type: WindowStateActions.Open;
  windowId: WindowId;
  params?: any;
}

export interface OpenAsPopup {
  type: WindowStateActions.OpenAsPopup;
  windowId: WindowId;
  asPopup: true;
  params?: any;
}

export interface PopIn {
  type: WindowStateActions.PopIn;
  windowId: WindowId;
  params?: any;
}

export interface Close {
  type: WindowStateActions.Close;
  windowId: WindowId | string;
}

export interface Minimize {
  type: WindowStateActions.Minimize;
  windowId: WindowId | string;
}

export interface Focus {
  type: WindowStateActions.Focus;
  windowId: WindowId | string;
}

export interface SendParams {
  type: WindowStateActions.SendParams;
  windowId: WindowId | string;
  params: any;
}

export interface Disconnect {
  type: WindowStateActions.Disconnect;
  windowId: WindowId | string;
  nextId: string;
}

export const setADMProps = (windowId: WindowId | string, admProps: any): SetADM => ({
  type: WindowStateActions.setADMProps,
  windowId,
  params: { admProps },
});

export const openWindow = (windowId: WindowId, params?: any): Open => ({
  type: WindowStateActions.Open,
  windowId,
  params,
});

export const popIn = (windowId: WindowId): PopIn => ({
  type: WindowStateActions.PopIn,
  windowId,
});

export const openAsPopup = (windowId: WindowId, params?: any): OpenAsPopup => ({
  type: WindowStateActions.OpenAsPopup,
  windowId,
  asPopup: true,
  params,
});

export const closeWindow = (windowId: WindowId): Close => ({
  type: WindowStateActions.Close,
  windowId,
});

export const sendParams = (windowId: WindowId, params: any): SendParams => ({
  type: WindowStateActions.SendParams,
  windowId,
  params,
});

export const minimizeWindow = (windowId: WindowId): Minimize => ({
  type: WindowStateActions.Minimize,
  windowId,
});

export const focusWindow = (windowId: WindowId): Focus => ({
  type: WindowStateActions.Focus,
  windowId,
});

export type Actions = Open | Close | Minimize | Focus | SendParams | Disconnect | OpenAsPopup | SetADM | PopIn;

export function windowReducer(state: State, action: Actions): State {
  switch (action.type) {
    case WindowStateActions.OpenAsPopup:
    case WindowStateActions.Open:


      StatTracker.open("Window", WindowId[action.windowId]);
      ActionTracker.track("WindowAction", "Window", "Open", { windowId: WindowId[action.windowId] }); // Only track open action
      if (state.windows.find(w => w.id === action.windowId)) {
        return {
          ...state,
          windows: state.windows.map(w =>
            w.id === action.windowId
              ? { ...w, state: WindowState.Open, windowParams: action.params, asPopop: "asPopup" in action ? action.asPopup : w.asPopop, updateCounter: w.updateCounter++ }
              : w
          ),
        };
      }
      return {
        ...state,
        windows: [
          ...state.windows,
          {
            id: action.windowId,
            state: WindowState.Open,
            windowParams: action.params,
            asPopop: "asPopup" in action ? action.asPopup : false,
            updateCounter: 1,
          },
        ],
      };
    case WindowStateActions.PopIn: {
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.windowId
            ? { ...w, asPopop: false, updateCounter: w.updateCounter++ }
            : w
        ),
      };
    }
    case WindowStateActions.Close:
      StatTracker.close("Window", WindowId[action.windowId]);
      return {
        ...state,
        windows: state.windows
          .filter(w => w.id !== action.windowId || w.alwayMounted == true)
          .map(w =>
            w.id === action.windowId ? { ...w, state: WindowState.Closed, asPopop: false } : w
          ),
      };
    case WindowStateActions.Minimize:
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.windowId
            ? {
              ...w,
              state: WindowState.Minimized,
              updateCounter: w.updateCounter++,
            }
            : w
        ),
      };
    case WindowStateActions.Focus:
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.windowId ? { ...w, state: WindowState.Open } : w
        ),
      };
    case WindowStateActions.SendParams:
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.windowId
            ? {
              ...w,
              windowParams: action.params,
              updateCounter: w.updateCounter++,
            }
            : w
        ),
      };
    case WindowStateActions.Disconnect:
      return {
        ...state,
        windows: state.windows.filter(w => w.id !== action.windowId),
      };
    case WindowStateActions.setADMProps:
      return {
        ...state,
        windows: state.windows.map(w =>
          w.id === action.windowId
            ? { ...w, adm: action.params.admProps, updateCounter: w.updateCounter++ }
            : w
        ),
      };
    default:
      return state;
  }
}
